import React, { useState, useEffect } from 'react';
import StudentImportModal from './StudentImportModal';
import ModalePlanning     from './ModalePlanning';
import PlanningTopbar     from './PlanningTopbar';
import ParamConfiguration from './setup/ParamConfiguration';
import ParamEvenements    from './setup/ParamEvenements';
import ParamProgressModal from './setup/ParamProgressModal';
import { ETAPES } from './PlanningEtapes';
import { estAdminDepartement, peutModifier } from '../services/droits';
import {
    departementsAPI, niveauxAPI, calendrierAPI,
    typesJourneesAPI, niveauxConfigAPI, semestresHeuresAPI, planningAPI
} from '../services/api';
import './PlanningApp.css';

/**
 * Paramétrage du Planning annuel — ex-`SetupWizard` (lot 5.4).
 *
 * Ce n'est plus un assistant : c'est l'onglet « Paramétrage » du module, avec ses
 * deux sous-onglets (Configuration / Événements) et un seul bouton d'action. Les
 * trois « modes » (`new` / `existing` / `modify`) ont disparu avec les trois jeux de
 * boutons qu'ils commandaient : la maquette n'en a qu'un, *Sauvegarder*, et le badge
 * « non sauvegardé » porte à lui seul l'information que les bannières répétaient.
 *
 * ── LES DEUX GARDE-FOUS CONTRE LA PERTE DE PÉRIODES ────────────────────────────
 *
 * `periodes_formation.sous_niveau_id` est en `ON DELETE CASCADE`, et cet écran
 * supprimait des `sous_niveaux` sans le dire. Deux mécanismes distincts :
 *
 *  D-b — RENOMMER NE DÉTRUIT PLUS.  Le code du groupe était dérivé de son NOM : le
 *        renommer produisait un code différent, donc une ligne créée et l'ancienne
 *        supprimée comme orpheline. Corriger une faute de frappe effaçait tout le
 *        planning du groupe, en silence. Les groupes sont désormais appariés par
 *        POSITION avec ceux déjà en base et modifiés EN PLACE (`PUT`), donc à
 *        identifiant constant : périodes, heures par semestre et rattachement des
 *        étudiants survivent. Idem pour le renommage d'un NIVEAU, qui changeait le
 *        `niveau_id` — le groupe suit son niveau au lieu d'être recréé.
 *
 *  D-a — SUPPRIMER LE DIT.  Baisser le compteur « Groupes » ou décocher un niveau
 *        reste une suppression volontaire, mais ses conséquences ne l'étaient pas
 *        forcément. Avant d'écrire quoi que ce soit, le serveur est interrogé sur ce
 *        qui va disparaître, et une modale l'annonce chiffré — avec la possibilité
 *        d'enregistrer d'abord une version restaurable du planning.
 *
 * L'ordre compte : on demande AVANT d'écrire. Un récapitulatif après coup n'est pas
 * un garde-fou, c'est un faire-part.
 *
 * ── LOT PA.1 : TROIS ÉTAPES, UNE SAUVEGARDE PAR ÉTAPE ──────────────────────────
 *
 * Les deux sous-onglets deviennent trois étapes du module (`onglet` = `etape1` ·
 * `etape2` · `etape3`), avec chacune SON bouton « Sauvegarder » (décidé le 24/10) :
 *   1 · l'année (lecture seule : c'est celle de la barre de gauche), les types de
 *       journées, le titre et le rappel de sauvegarde ;
 *   2 · les catégories d'événements — elles s'enregistrent déjà une à une, dans
 *       leur propre fenêtre : pas de bouton de plus ;
 *   3 · les niveaux, groupes et heures, avec les deux garde-fous ci-dessus.
 * L'étape 3 n'écrit plus les types, l'étape 1 n'écrit plus les groupes : une erreur
 * de saisie dans l'une ne bloque plus l'enregistrement de l'autre. Les colonnes
 * d'heures de l'étape 3 suivent donc les types ENREGISTRÉS (`typesEnregistres`) :
 * un type ajouté à l'étape 1 y apparaît une fois l'étape 1 sauvegardée. Rien ne
 * relie les heures aux types en base (pas de clé étrangère), mais saisir des heures
 * pour un type qui n'existe pas encore en laisserait d'orphelines.
 */

const TYPES_DEFAUT = [
    { code: 'F', libelle: 'Formation',  couleur: '#1e293b', couleur_texte: '#FFFFFF', controle: true,  heures_par_jour: 7 },
    { code: 'E', libelle: 'Entreprise', couleur: '#94a3b8', couleur_texte: '#FFFFFF', controle: true,  heures_par_jour: 7 },
    { code: 'S', libelle: 'Stage',      couleur: '#7C3AED', couleur_texte: '#FFFFFF', controle: false, heures_par_jour: 0 }
];

const NIVEAUX_DEFAUT = [
    { code: 'BUT1', nom: 'BUT 1ère année', semestres: ['S1', 'S2'] },
    { code: 'BUT2', nom: 'BUT 2ème année', semestres: ['S3', 'S4'] },
    { code: 'BUT3', nom: 'BUT 3ème année', semestres: ['S5', 'S6'] }
];

const COULEURS = ['#007BFF','#28A745','#FFC107','#DC3545','#6F42C1','#FD7E14','#20C997','#E83E8C','#0DCAF0','#198754'];

const getSemestresForNiveau = (code) => {
    if (code === 'BUT1') return ['S1', 'S2'];
    if (code === 'BUT2') return ['S3', 'S4'];
    if (code === 'BUT3') return ['S5', 'S6'];
    return ['S1', 'S2'];
};

const niveauxDefautInit = () =>
    NIVEAUX_DEFAUT.map((n, i) => ({
        id: null, code: n.code, nom: n.nom, selected: true, nbGroupes: 1,
        groupes: [{ nom: '', couleur: COULEURS[i % COULEURS.length], semestresHeures: {} }],
        semestres: n.semestres
    }));

/** Message d'erreur lisible d'une réponse serveur — jamais un objet brut à l'écran. */
const messageErreur = (err, repli) =>
    err?.response?.data?.error || err?.response?.data?.details || err?.message || repli;

function PlanningParametrage({
    user, config, initialDept, externalAnneeDebut,
    onglet, onChangeOnglet, calendrierPret,
    onConfigEnregistree, onEnregistre,
}) {
    const etape = Number(String(onglet || '').replace('etape', '')) || 1;
    const definitionEtape = ETAPES.find(e => e.n === etape);

    const [anneeDebut, setAnneeDebut]           = useState(externalAnneeDebut || new Date().getFullYear());
    const [departements, setDepartements]       = useState([]);
    const [selectedDept, setSelectedDept]       = useState(null);
    const [titrePlanning, setTitrePlanning]     = useState('');
    const [rappelSauvegarde, setRappelSauvegarde] = useState('30');
    const [typesJournees, setTypesJournees]     = useState(TYPES_DEFAUT.map(t => ({ ...t })));
    // Ce qui est EN BASE pour l'étape 1 (lot PA.1) : l'étape 3 publie la configuration
    // avec ces valeurs-là, et non avec une saisie de l'étape 1 pas encore sauvegardée.
    const [typesEnregistres, setTypesEnregistres] = useState(TYPES_DEFAUT.map(t => ({ ...t })));
    const [reglagesEnregistres, setReglagesEnregistres] = useState({ titre: '', rappel: '30' });
    // false = aucun type en base : l'écran montre TYPES_DEFAUT (département neuf)
    const [typesEnBase, setTypesEnBase] = useState(true);
    const [niveauxConfig, setNiveauxConfig]     = useState([]);
    // Photo des sous-niveaux TELS QU'ILS SONT EN BASE au chargement. C'est la
    // référence des deux garde-fous : sans elle, un groupe retiré de l'écran par le
    // compteur « Groupes » disparaît aussi de `niveauxConfig`, et plus rien ne permet
    // de savoir qu'il existait — ni de dire ce que sa suppression va coûter.
    const [sousNiveauxCharges, setSousNiveauxCharges] = useState([]);

    const [loading, setLoading]                 = useState(false);
    const [loadingExisting, setLoadingExisting] = useState(false);
    const [message, setMessage]                 = useState('');
    const [error, setError]                     = useState('');
    // « non sauvegardé » PAR ÉTAPE (lot PA.1) : { 1: bool, 3: bool }
    const [modifie, setModifie]                 = useState({});

    const [showProgress, setShowProgress]       = useState(false);
    const [progressStep, setProgressStep]       = useState(0);
    const [progressMessage, setProgressMessage] = useState('');

    const [importModalOpen, setImportModalOpen]                         = useState(false);
    const [selectedSousNiveauForImport, setSelectedSousNiveauForImport] = useState(null);
    const [allAnnees, setAllAnnees]                                     = useState([]);
    // `importingEvents` a disparu avec la carte d'import N-1 (lot 5.4 ter).

    // Garde-fou D-a : le récapitulatif de ce qui va disparaître, avant d'écrire.
    const [impactSuppression, setImpactSuppression] = useState(null);
    const [versionEnCours, setVersionEnCours]       = useState(false);

    const userDepts    = user?.departements || [];
    const isSingleDept = userDepts.length === 1;
    const isSuper      = user?.is_super_admin === true;
    // Les colonnes d'heures de l'étape 3 : les types ENREGISTRÉS (cf. en-tête, lot PA.1).
    const typesControles = typesEnregistres.filter(t => t.controle && t.heures_par_jour > 0);

    // Écrire ici, c'est écrire la configuration du DÉPARTEMENT : les routes
    // `/niveaux-config`, `/types-journees` et `/semestres-heures` sont montées sous
    // `ecritureDeptAdmin`. La lecture seule se calcule donc sur ce droit-là, et non
    // sur le droit « planning » qui gouverne le Calendrier.
    const lectureSeule = !!selectedDept && !estAdminDepartement(user, selectedDept.id);

    // ⚠️ LE RÉFÉRENTIEL DES ÉVÉNEMENTS NE RELÈVE PAS DU MÊME DROIT (lot 5.4 bis).
    // Les routes `/api/evenements` sont montées sous `requireModule('planning')`,
    // pas sous `ecritureDeptAdmin`. Un enseignant qui a « modifier » sur le Planning
    // crée déjà des événements types depuis le panneau du Calendrier : les lui
    // griser ici aurait interdit dans un écran ce qui est permis dans l'autre — et
    // le serveur, lui, aurait accepté. L'interface doit dire la même chose que lui.
    const lectureSeuleEvenements = !!selectedDept && !peutModifier(user, selectedDept.id, 'planning');

    // Étape 1 : types de journées, titre, rappel. Étape 3 : niveaux, groupes, heures.
    const marquer = (n) => setModifie(m => (m[n] ? m : { ...m, [n]: true }));
    const marquerEtape1 = () => marquer(1);
    const markModified = () => marquer(3);

    // Le message d'une étape ne se lit pas sur la suivante (« Formations enregistrées »
    // affiché à l'étape 1 laisserait croire que l'étape 1 l'est aussi).
    useEffect(() => { setMessage(''); setError(''); }, [etape]);

    // ── Chargement ──────────────────────────────────────────────────────────

    useEffect(() => {
        if (externalAnneeDebut && externalAnneeDebut !== anneeDebut) setAnneeDebut(externalAnneeDebut);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [externalAnneeDebut]);

    useEffect(() => { fetchDepartements(); /* eslint-disable-next-line */ }, []);

    const fetchDepartements = async () => {
        try {
            const deptDeLaConfig = config?.departement || initialDept || null;
            if (isSingleDept) {
                setDepartements(userDepts);
                setSelectedDept(userDepts[0]);
            } else if (!isSuper && userDepts.length > 1) {
                setDepartements(userDepts);
                const found = deptDeLaConfig ? userDepts.find(d => d.id === deptDeLaConfig.id) : null;
                setSelectedDept(found || userDepts[0]);
            } else {
                const response = await departementsAPI.getAll();
                const depts = response.data.departements;
                setDepartements(depts);
                const found = deptDeLaConfig ? depts.find(d => d.id === deptDeLaConfig.id) : null;
                setSelectedDept(found || depts[0] || null);
            }
        } catch (err) {
            setError('Impossible de charger la liste des départements : ' + messageErreur(err, ''));
        }
    };

    const loadTypesJournees = async (dept) => {
        let liste;
        try {
            const response = await typesJourneesAPI.getAll(dept.id);
            const types = response.data.types;
            setTypesEnBase(!!(types && types.length > 0));
            liste = types && types.length > 0
                ? types.map(t => ({
                    code: t.code.trim(), libelle: t.libelle, couleur: t.couleur,
                    couleur_texte: t.couleur_texte || '#FFFFFF',
                    controle: t.controle !== undefined ? t.controle : true,
                    heures_par_jour: t.heures_par_jour !== undefined ? t.heures_par_jour : 7
                }))
                : TYPES_DEFAUT.map(t => ({ ...t }));
        } catch {
            liste = TYPES_DEFAUT.map(t => ({ ...t }));
        }
        setTypesJournees(liste);
        setTypesEnregistres(liste.map(t => ({ ...t })));
    };

    const loadNiveauxConfig = async (dept) => {
        try {
            const niveaux     = (await niveauxConfigAPI.getAll(dept.id)).data.niveaux;
            const sousNiveaux = (await niveauxAPI.getSousNiveaux(dept.id)).data.sous_niveaux;
            setSousNiveauxCharges(sousNiveaux);

            let heuresParType = [];
            try {
                heuresParType = (await semestresHeuresAPI.getByDepartement(dept.id)).data.heures || [];
            } catch { /* aucune heure enregistrée : les champs restent vides */ }

            const parNiveau = {};
            sousNiveaux.forEach(sn => {
                if (!parNiveau[sn.niveau_id]) parNiveau[sn.niveau_id] = [];
                parNiveau[sn.niveau_id].push(sn);
            });
            Object.values(parNiveau).forEach(l => l.sort((a, b) => (a.ordre || 0) - (b.ordre || 0)));

            const configNiveaux = niveaux.map((n, idx) => {
                const niveauId  = n.niveau_id || n.id;
                const snList    = parNiveau[niveauId] || [];
                const semestres = getSemestresForNiveau(n.code);

                if (snList.length === 0) {
                    return {
                        id: niveauId, code: n.code, nom: n.nom, selected: true, nbGroupes: 1,
                        groupes: [{ nom: '', couleur: COULEURS[idx % COULEURS.length], semestresHeures: {} }],
                        semestres
                    };
                }

                const groupes = snList.map(sn => {
                    let groupeNom = '';
                    if (sn.nom && sn.nom.startsWith(n.code)) {
                        const parts = sn.nom.split(' ');
                        if (parts.length > 1) groupeNom = parts.slice(1).join(' ');
                    } else {
                        groupeNom = sn.code;
                    }

                    const semestresHeures = {};
                    semestres.forEach(sem => {
                        semestresHeures[sem] = {};
                        heuresParType
                            .filter(h => h.sous_niveau_id === sn.id && h.semestre === sem)
                            .forEach(h => { semestresHeures[sem][h.type_code.trim()] = parseFloat(h.heures); });
                    });

                    return { id: sn.id, nom: groupeNom, couleur: sn.couleur || '#007BFF', semestresHeures };
                });

                return {
                    id: niveauId, code: n.code, nom: n.nom, selected: true,
                    nbGroupes: snList.length, groupes, semestres
                };
            });

            setNiveauxConfig(configNiveaux.length > 0 ? configNiveaux : niveauxDefautInit());
        } catch (err) {
            setError('Impossible de charger les niveaux : ' + messageErreur(err, ''));
            setNiveauxConfig(niveauxDefautInit());
        }
    };

    const chargerDepartement = async (dept, annee) => {
        if (!dept) return;
        setLoadingExisting(true);
        setMessage(''); setError(''); setModifie({});
        try {
            try {
                const deptResponse = await departementsAPI.getById(dept.id);
                const titre = deptResponse.data.departement.titre_planning || '';
                // `?? 30` et non `|| 30` : 0 est une valeur VOULUE (rappel désactivé).
                const rappel = String(deptResponse.data.departement.rappel_sauvegarde_minutes ?? 30);
                setTitrePlanning(titre);
                setRappelSauvegarde(rappel);
                setReglagesEnregistres({ titre, rappel });
            } catch { /* le département reste lisible sans son titre */ }

            await loadTypesJournees(dept);
            await loadNiveauxConfig(dept);

            try {
                const liste = (await calendrierAPI.getAll()).data?.annees || [];
                setAllAnnees(liste);
            } catch (err) {
                setError("Impossible de charger les années universitaires : " + messageErreur(err, ''));
            }
        } finally {
            setLoadingExisting(false);
        }
    };

    useEffect(() => {
        if (selectedDept) chargerDepartement(selectedDept, anneeDebut);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDept, anneeDebut]);

    const anneeSelectionnee = allAnnees.find(
        a => parseInt((a.libelle || '').split('-')[0], 10) === anneeDebut
    ) || null;
    // `prevAnneeObj` a disparu avec la carte « Événements de l'année précédente »
    // (lot 5.4 ter) : elle ne servait qu'à elle. La reprise de N-1 se fait depuis le
    // panneau du Calendrier, qui permet de CHOISIR ce qu'on reprend.

    // ── Handlers de saisie ─────────────────────────────────────────────────

    const openImportModal = (sousNiveauId, sousNiveauNom) => {
        setSelectedSousNiveauForImport({ id: sousNiveauId, nom: sousNiveauNom });
        setImportModalOpen(true);
    };

    const addNiveau = () => {
        const n = niveauxConfig.length + 1;
        setNiveauxConfig([...niveauxConfig, {
            id: null, code: 'NIV' + n, nom: 'Niveau ' + n, selected: true, nbGroupes: 1,
            groupes: [{ nom: '', couleur: COULEURS[niveauxConfig.length % COULEURS.length], semestresHeures: {} }],
            semestres: ['S' + (n * 2 - 1), 'S' + (n * 2)]
        }]);
        markModified();
    };

    const removeNiveau = (index) => {
        if (niveauxConfig.filter(n => n.selected).length <= 1 && niveauxConfig[index].selected) {
            setError('Au moins un niveau actif est requis.');
            return;
        }
        setNiveauxConfig(niveauxConfig.filter((_, i) => i !== index));
        markModified();
    };

    const majNiveau = (index, transformation) => {
        const updated = [...niveauxConfig];
        updated[index] = { ...updated[index], ...transformation(updated[index]) };
        setNiveauxConfig(updated);
        markModified();
    };

    const updateNiveauCode = (index, code) => {
        const propre = code.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 10);
        majNiveau(index, () => ({ code: propre, semestres: getSemestresForNiveau(propre) }));
    };
    const updateNiveauNom = (index, nom) => majNiveau(index, () => ({ nom }));
    const toggleNiveau    = (index) => majNiveau(index, (n) => ({ selected: !n.selected }));

    const moveNiveau = (index, direction) => {
        const cible = index + direction;
        if (cible < 0 || cible >= niveauxConfig.length) return;
        const updated = [...niveauxConfig];
        [updated[index], updated[cible]] = [updated[cible], updated[index]];
        setNiveauxConfig(updated);
        markModified();
    };

    const updateNbGroupes = (niveauIndex, nb) => {
        const nbGroupes = Math.max(1, Math.min(10, parseInt(nb, 10) || 1));
        const updated   = [...niveauxConfig];
        const niveau    = updated[niveauIndex];
        updated[niveauIndex] = {
            ...niveau,
            nbGroupes,
            // Les entrées conservées gardent leur `id` : c'est lui qui permet le
            // renommage en place et le calcul de ce qui va vraiment disparaître.
            groupes: Array.from({ length: nbGroupes }, (_, i) => {
                const existant   = niveau.groupes[i];
                const nomParDefaut = nbGroupes === 1 ? niveau.code : '';
                return existant
                    ? { ...existant, nom: existant.nom || nomParDefaut }
                    : { nom: nomParDefaut, couleur: COULEURS[i % COULEURS.length], semestresHeures: {} };
            })
        };
        setNiveauxConfig(updated);
        markModified();
    };

    const majGroupe = (niveauIndex, groupeIndex, transformation) => {
        const updated = [...niveauxConfig];
        const groupes = [...updated[niveauIndex].groupes];
        groupes[groupeIndex] = { ...groupes[groupeIndex], ...transformation(groupes[groupeIndex]) };
        updated[niveauIndex] = { ...updated[niveauIndex], groupes };
        setNiveauxConfig(updated);
        markModified();
    };

    const updateGroupeNom     = (ni, gi, nom)     => majGroupe(ni, gi, () => ({ nom }));
    const updateGroupeCouleur = (ni, gi, couleur) => majGroupe(ni, gi, () => ({ couleur }));

    const updateGroupeHeures = (ni, gi, semestre, typeCode, value) => {
        majGroupe(ni, gi, (g) => {
            const semestresHeures = { ...(g.semestresHeures || {}) };
            semestresHeures[semestre] = { ...(semestresHeures[semestre] || {}), [typeCode]: parseFloat(value) || 0 };
            return { semestresHeures };
        });
    };

    const addType = () => {
        const utilises = typesJournees.map(t => t.code);
        const suivant  = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').find(c => !utilises.includes(c));
        if (!suivant) { setError('Maximum 26 types de journées (A-Z).'); return; }
        setTypesJournees([...typesJournees, {
            code: suivant, libelle: '', couleur: '#6B7280', couleur_texte: '#FFFFFF',
            controle: false, heures_par_jour: 0
        }]);
        marquerEtape1();
    };

    const removeType = (index) => {
        if (typesJournees.length <= 1) { setError('Au moins un type de journée est requis.'); return; }
        setTypesJournees(typesJournees.filter((_, i) => i !== index));
        marquerEtape1();
    };

    const updateType = (index, field, value) => {
        const updated = [...typesJournees];
        if (field === 'code') {
            value = value.toUpperCase().replace(/[^A-Z]/g, '').substring(0, 1);
            if (value && typesJournees.some((t, i) => i !== index && t.code === value)) {
                setError('Le code « ' + value + ' » est déjà utilisé.');
                return;
            }
        }
        updated[index] = field === 'controle'
            ? { ...updated[index], controle: value, heures_par_jour: value ? (updated[index].heures_par_jour || 7) : 0 }
            : { ...updated[index], [field]: value };
        setTypesJournees(updated);
        setError('');
        marquerEtape1();
    };

    // ── Validation ─────────────────────────────────────────────────────────

    // Lot PA.1 : une validation PAR ÉTAPE — l'étape 1 ne bute plus sur un groupe mal
    // nommé de l'étape 3, ni l'inverse.
    const validerEtape3 = () => {
        setError('');
        if (!selectedDept) { setError('Sélectionnez un département.'); return false; }

        const actifs = niveauxConfig.filter(n => n.selected);
        if (actifs.length === 0) { setError('Sélectionnez au moins un niveau.'); return false; }
        if (new Set(actifs.map(n => n.code)).size !== actifs.length) {
            setError('Les codes de niveaux doivent être uniques.'); return false;
        }
        for (const niveau of actifs) {
            if (!niveau.code?.trim()) { setError('Chaque niveau doit avoir un code.'); return false; }
            if (!niveau.nom?.trim())  { setError('Chaque niveau doit avoir un nom.');  return false; }
            if (niveau.nbGroupes > 1) {
                for (const g of niveau.groupes) {
                    if (!g.nom.trim()) { setError('Nommez tous les groupes de ' + niveau.code + '.'); return false; }
                }
                const noms = niveau.groupes.map(g => g.nom.trim().toUpperCase());
                if (new Set(noms).size !== noms.length) {
                    setError('Deux groupes de ' + niveau.code + ' portent le même nom.'); return false;
                }
            }
        }
        return true;
    };

    const validerEtape1 = () => {
        setError('');
        if (!selectedDept) { setError('Sélectionnez un département.'); return false; }
        if (typesJournees.length === 0) { setError('Au moins un type de journée est requis.'); return false; }
        for (const t of typesJournees) {
            if (!t.code || !/^[A-Z]$/.test(t.code)) { setError('Chaque type de journée doit avoir un code (A-Z).'); return false; }
            if (!t.libelle?.trim()) { setError('Chaque type de journée doit avoir un libellé.'); return false; }
        }

        // Vérifié AVANT d'envoyer quoi que ce soit : sans ce contrôle, les types et
        // les niveaux s'enregistraient puis le serveur refusait le rappel — une
        // sauvegarde à moitié faite (défaut du 2026-08-08).
        const r = parseInt(rappelSauvegarde, 10);
        if (rappelSauvegarde !== '' && (!Number.isInteger(r) || r < 0 || r > 240)) {
            setError('Rappel de sauvegarde : saisissez 0 pour le désactiver, ou une valeur entre 1 et 240 minutes.');
            return false;
        }
        return true;
    };

    // ── Garde-fou D-a : ce qui va disparaître ──────────────────────────────

    /**
     * Identifiants des sous-niveaux déjà en base qu'une sauvegarde supprimerait.
     * Un groupe SURVIT s'il est encore présent, à sa position, dans un niveau coché.
     * Renommé, déplacé d'un niveau à l'autre : il survit — c'est le garde-fou D-b.
     */
    const idsQuiVontDisparaitre = () => {
        const conserves = new Set();
        niveauxConfig
            .filter(n => n.selected)
            .forEach(n => n.groupes.slice(0, n.nbGroupes).forEach(g => { if (g.id) conserves.add(g.id); }));
        return sousNiveauxCharges.filter(sn => !conserves.has(sn.id)).map(sn => sn.id);
    };

    const handleSauvegarder = async () => {
        if (etape === 1) { await sauverEtape1(); return; }
        if (!validerEtape3()) return;

        const ids = idsQuiVontDisparaitre();
        if (ids.length === 0) { await executerSauvegarde(); return; }

        setLoading(true);
        try {
            const { data } = await niveauxAPI.impactSuppression(ids);
            const t = data.totaux || {};
            // Rien de rattaché : la suppression est sans conséquence, on n'interrompt
            // pas l'utilisateur pour lui annoncer qu'il ne se passe rien.
            if (!t.periodes && !t.etudiants && !t.evenementsDates) {
                setLoading(false);
                await executerSauvegarde();
                return;
            }
            setImpactSuppression({ ids, impacts: data.impacts || [], totaux: t });
        } catch (err) {
            setError("Impossible de vérifier ce que la sauvegarde supprimerait : "
                + messageErreur(err, '') + " — sauvegarde interrompue par précaution.");
        } finally {
            setLoading(false);
        }
    };

    /**
     * « Enregistrer une version d'abord » : le filet du lot 5.3, proposé au moment
     * exact où il sert. La version porte les périodes et les événements placés — donc
     * précisément ce que la cascade va emporter.
     */
    const handleEnregistrerVersionDAbord = async () => {
        if (!selectedDept || !anneeSelectionnee) {
            setError("Aucune année sélectionnée : impossible d'enregistrer une version.");
            return;
        }
        setVersionEnCours(true);
        try {
            // Le filtre global des colonnes vit en localStorage : c'est le navigateur
            // qui le fournit à l'instantané (même clé que le Calendrier).
            let filtres = {};
            try {
                filtres = JSON.parse(
                    localStorage.getItem(`vis_${selectedDept.id}_${anneeSelectionnee.id}`) || '{}'
                );
            } catch { filtres = {}; }

            await planningAPI.saveVersion(
                selectedDept.id,
                anneeSelectionnee.id,
                'Avant modification du Paramétrage',
                filtres
            );
            setMessage('Version « Avant modification du Paramétrage » enregistrée. '
                + 'Elle est restaurable depuis l\'horloge du Calendrier.');
        } catch (err) {
            setError("La version n'a pas pu être enregistrée : " + messageErreur(err, '')
                + ' — rien n\'a été modifié.');
            setImpactSuppression(null);
        } finally {
            setVersionEnCours(false);
        }
    };

    // ── Sauvegarde ─────────────────────────────────────────────────────────

    const sauvegarderTypesJournees = async () => {
        await typesJourneesAPI.save(selectedDept.id, typesJournees.map(t => ({
            code: t.code, libelle: t.libelle, couleur: t.couleur, couleur_texte: t.couleur_texte,
            controle: t.controle, heures_par_jour: t.heures_par_jour
        })));
    };

    /**
     * Enregistre niveaux, groupes et heures — c'est ici que vit le garde-fou D-b.
     *
     * Appariement par POSITION avec les sous-niveaux chargés, et non par code : c'est
     * tout le correctif. Par code, « A » renommé « A1 » ne s'appariait à rien, donc
     * création + suppression de l'orphelin, donc cascade sur les périodes.
     */
    const sauvegarderNiveauxConfig = async (actifs) => {
        await niveauxConfigAPI.save(selectedDept.id, actifs.map((n, i) => ({ code: n.code, nom: n.nom, ordre: i + 1 })));

        const niveauIdMap = {};
        (await niveauxConfigAPI.getAll(selectedDept.id)).data.niveaux
            .forEach(ns => { niveauIdMap[ns.code] = ns.niveau_id; });

        const chargesParNiveau = {};
        sousNiveauxCharges.forEach(sn => {
            if (!chargesParNiveau[sn.niveau_id]) chargesParNiveau[sn.niveau_id] = [];
            chargesParNiveau[sn.niveau_id].push(sn);
        });
        Object.values(chargesParNiveau).forEach(l => l.sort((a, b) => (a.ordre || 0) - (b.ordre || 0)));

        const conserves = new Set();
        const enregistres = [];

        for (const niveau of actifs) {
            const niveauIdCible = niveauIdMap[niveau.code] || niveau.id;
            if (!niveauIdCible) continue;

            const existants = (chargesParNiveau[niveau.id] || []).slice(0, niveau.nbGroupes);

            const voulus = niveau.groupes.slice(0, niveau.nbGroupes).map((groupe, i) => ({
                code: niveau.nbGroupes === 1
                    ? (groupe.nom.trim() ? groupe.nom.trim().replace(/\s+/g, '_').toUpperCase().substring(0, 20) : 'FI')
                    : groupe.nom.replace(/\s+/g, '_').toUpperCase().substring(0, 20),
                nom: niveau.nbGroupes === 1
                    ? niveau.code + (groupe.nom.trim() ? ' ' + groupe.nom.trim() : '')
                    : niveau.code + ' ' + groupe.nom,
                couleur: groupe.couleur,
                ordre: i + 1,
                semestresHeures: groupe.semestresHeures,
                existant: existants[i] || null
            }));

            // Échanger les noms de deux groupes (« A » ↔ « B ») ferait viser à chacun
            // le code que l'autre porte encore : la contrainte d'unicité refuserait le
            // premier `PUT`, au milieu d'une sauvegarde déjà commencée. On passe alors
            // par des codes temporaires. Détecté, et pas systématique : deux requêtes
            // de plus par groupe ne se paient que dans le cas qui les exige.
            const codesActuels = new Set(existants.map(e => e.code));
            const collisionCroisee = voulus.some(v =>
                v.existant && v.code !== v.existant.code && codesActuels.has(v.code)
            );
            if (collisionCroisee) {
                for (const v of voulus) {
                    if (!v.existant || v.code === v.existant.code) continue;
                    await niveauxAPI.updateSousNiveau(v.existant.id, {
                        code: ('TMP' + v.existant.id).substring(0, 20),
                        nom: v.existant.nom,
                        couleur: v.existant.couleur,
                        ordre: v.existant.ordre,
                        heures_totales: v.existant.heures_totales,
                        niveauId: v.existant.niveau_id
                    });
                }
            }

            for (const v of voulus) {
                let id;
                if (v.existant) {
                    // MODIFICATION EN PLACE — l'identifiant ne change pas, donc les
                    // périodes, les heures et les étudiants restent rattachés.
                    const r = await niveauxAPI.updateSousNiveau(v.existant.id, {
                        code: v.code, nom: v.nom, couleur: v.couleur, ordre: v.ordre,
                        heures_totales: v.existant.heures_totales,
                        niveauId: niveauIdCible
                    });
                    id = r.data.sous_niveau.id;
                } else {
                    const r = await niveauxAPI.createSousNiveau({
                        departementId: selectedDept.id, niveauId: niveauIdCible,
                        code: v.code, nom: v.nom, couleur: v.couleur, ordre: v.ordre
                    });
                    id = r.data.sous_niveau.id;
                }
                conserves.add(id);
                enregistres.push({ id, semestresHeures: v.semestresHeures });
            }
        }

        // Suppressions : seulement ce qui n'a pas été apparié — compteur « Groupes »
        // baissé, niveau décoché ou retiré. Elles ont été annoncées par D-a.
        for (const sn of sousNiveauxCharges) {
            if (!conserves.has(sn.id)) await niveauxAPI.deleteSousNiveau(sn.id);
        }

        const entrees = [];
        for (const sn of enregistres) {
            for (const [semestre, heuresParType] of Object.entries(sn.semestresHeures || {})) {
                for (const [typeCode, heures] of Object.entries(heuresParType)) {
                    if (typeCode === 'autonomie' || typeCode === 'presentiel') continue;
                    entrees.push({ sousNiveauId: sn.id, semestre, typeCode, heures: parseFloat(heures) || 0 });
                }
            }
        }
        if (entrees.length > 0) await semestresHeuresAPI.saveBatch(selectedDept.id, entrees);
    };

    /**
     * Reconstruit la configuration du module et la remonte — sans naviguer.
     * Lot PA.1 : avec les valeurs ENREGISTRÉES de l'étape 1, jamais une saisie de
     * l'étape 1 restée en cours pendant qu'on sauvegarde l'étape 3.
     */
    const publierConfig = async (actifs, anneeScolaireId, reglages = reglagesEnregistres, types = typesEnregistres) => {
        const sousNiveaux = (await niveauxAPI.getSousNiveaux(selectedDept.id)).data.sous_niveaux;
        const niveauIdMap = {};
        (await niveauxConfigAPI.getAll(selectedDept.id)).data.niveaux
            .forEach(ns => { niveauIdMap[ns.code] = ns.niveau_id; });

        onConfigEnregistree({
            anneeScolaireId,
            // `selectedDept` vient de `user.departements` : il ne porte que l'IDENTITÉ
            // du département. Tout réglage lu par le Planning doit être ajouté ici
            // explicitement — sinon il existe en base, s'enregistre, et n'arrive jamais
            // à l'écran (ce qui est arrivé au rappel de sauvegarde le 2026-08-08).
            departement: {
                ...selectedDept,
                titre_planning: reglages.titre,
                rappel_sauvegarde_minutes: parseInt(reglages.rappel, 10) || 0
            },
            sousNiveaux,
            typesJournees: types,
            niveaux: actifs.map(n => {
                const niveauId   = niveauIdMap[n.code] || n.id;
                const snDuNiveau = sousNiveaux
                    .filter(sn => sn.niveau_id === niveauId)
                    .sort((a, b) => a.ordre - b.ordre);
                let colonnes;
                if (snDuNiveau.length === 1) {
                    colonnes = [{ label: n.code, code: snDuNiveau[0].code, couleur: snDuNiveau[0].couleur || '#007BFF' }];
                } else if (snDuNiveau.length > 1) {
                    colonnes = snDuNiveau.map(sn => ({
                        label: n.code + ' ' + (sn.nom.startsWith(n.code) ? sn.nom.substring(n.code.length + 1) : sn.code),
                        code: sn.code,
                        couleur: sn.couleur || '#007BFF'
                    }));
                } else {
                    colonnes = [{ label: n.code, code: 'FI', couleur: n.groupes[0]?.couleur || '#007BFF' }];
                }
                return { id: niveauId, code: n.code, nom: n.nom, nbGroupes: n.nbGroupes, colonnes };
            })
        });
    };

    /**
     * Étape 1 — types de journées, titre, rappel. N'écrit ni niveaux ni groupes.
     * La configuration du module est republiée avec ses groupes INCHANGÉS (ceux de
     * `config`) : seuls les types et les réglages du département y bougent. Sans
     * configuration encore (département neuf), il n'y a rien à republier — c'est
     * l'étape 3 qui la crée.
     */
    const sauverEtape1 = async () => {
        if (!validerEtape1()) return;
        setLoading(true); setMessage(''); setError('');
        try {
            await sauvegarderTypesJournees();
            await departementsAPI.update(selectedDept.id, {
                titrePlanning: titrePlanning || null,
                rappelSauvegardeMinutes: rappelSauvegarde
            });
            const types = typesJournees.map(t => ({ ...t }));
            const reglages = { titre: titrePlanning, rappel: rappelSauvegarde };
            setTypesEnregistres(types);
            setTypesEnBase(true);
            setReglagesEnregistres(reglages);
            if (config && config.departement?.id === selectedDept.id) {
                onConfigEnregistree({
                    ...config,
                    typesJournees: types,
                    departement: {
                        ...config.departement,
                        titre_planning: reglages.titre,
                        rappel_sauvegarde_minutes: parseInt(reglages.rappel, 10) || 0
                    }
                });
            }
            setMessage('Configuration enregistrée : types de journées, titre et rappel.');
            setModifie(m => ({ ...m, 1: false }));
            onEnregistre?.();
        } catch (err) {
            setError('Erreur : ' + messageErreur(err, "l'enregistrement a échoué"));
        } finally {
            setLoading(false);
        }
    };

    /** Étape 3 — niveaux, groupes et heures (après le garde-fou D-a). */
    const executerSauvegarde = async () => {
        setImpactSuppression(null);
        if (!anneeSelectionnee) {
            setError('Aucune année universitaire sélectionnée. Créez-la dans Administration → Calendriers.');
            return;
        }

        const actifs = niveauxConfig.filter(n => n.selected);
        setLoading(true); setShowProgress(true); setProgressStep(0); setMessage(''); setError('');
        try {
            // Département neuf : aucun type en base, l'écran montrait les types par
            // défaut. Avant le lot PA.1, la sauvegarde unique les écrivait : on garde
            // ce comportement, sans quoi le calendrier recevrait des types inexistants.
            if (!typesEnBase) {
                setProgressMessage('Types de journées par défaut…'); setProgressStep(20);
                await typesJourneesAPI.save(selectedDept.id, typesEnregistres.map(t => ({
                    code: t.code, libelle: t.libelle, couleur: t.couleur, couleur_texte: t.couleur_texte,
                    controle: t.controle, heures_par_jour: t.heures_par_jour
                })));
                setTypesEnBase(true);
            }

            setProgressMessage('Niveaux, groupes et heures…'); setProgressStep(55);
            await sauvegarderNiveauxConfig(actifs);

            setProgressMessage('Configuration du module…'); setProgressStep(90);
            // On recharge la photo AVANT de publier : les identifiants ont pu changer
            // (groupes créés), et la prochaine sauvegarde s'appuie sur cette photo.
            await loadNiveauxConfig(selectedDept);
            await publierConfig(actifs, anneeSelectionnee.id);

            setProgressStep(100); setProgressMessage('Enregistré.');
            await new Promise(r => setTimeout(r, 500));
            setMessage('Formations enregistrées : niveaux, groupes et heures.');
            setModifie(m => ({ ...m, 3: false }));
            onEnregistre?.();
        } catch (err) {
            setError('Erreur : ' + messageErreur(err, "l'enregistrement a échoué"));
        } finally {
            setShowProgress(false); setLoading(false);
        }
    };

    // `handleImportFromPrevYear` a été retiré au lot 5.4 ter avec la carte qu'il
    // servait. Il y avait DEUX portes « récupérer les événements N-1 » : celle-ci et
    // celle du panneau du Calendrier. La seconde est meilleure — elle permet de
    // CHOISIR ce qu'on reprend, là où celle-ci refusait dès que l'année contenait un
    // événement. Le remplacement était donc en place avant qu'on retire l'ancien.

    // ── Rendu ──────────────────────────────────────────────────────────────

    const proprietesVolets = {
        // L'année n'est plus choisie ici (lot PA.1) : c'est celle de la barre de gauche.
        allAnnees, anneeSelectionnee, anneeAttendue: `${anneeDebut}-${anneeDebut + 1}`,
        departements, selectedDept, setSelectedDept, isSingleDept, loadingExisting,
        titrePlanning,
        setTitrePlanning: (v) => { setTitrePlanning(v); marquerEtape1(); },
        rappelSauvegarde,
        setRappelSauvegarde: (v) => { setRappelSauvegarde(v); marquerEtape1(); },
        typesJournees, typesControles,
        onAdd: addType, onRemove: removeType, onUpdate: updateType,
        niveauxConfig,
        onToggleNiveau: toggleNiveau, onMoveNiveau: moveNiveau, onRemoveNiveau: removeNiveau,
        onAddNiveau: addNiveau, onUpdateNiveauCode: updateNiveauCode, onUpdateNiveauNom: updateNiveauNom,
        onUpdateNbGroupes: updateNbGroupes, onUpdateGroupeNom: updateGroupeNom,
        onUpdateGroupeCouleur: updateGroupeCouleur, onUpdateGroupeHeures: updateGroupeHeures,
        onOpenImportModal: openImportModal,
        lectureSeule,
    };

    return (
        <div className="pl-root">
            <ParamProgressModal show={showProgress} step={progressStep} message={progressMessage} />

            <div className="sticky-top" style={{ margin: 0, top: 0 }}>
                <PlanningTopbar
                    sousTitre={
                        (selectedDept?.nom || '') +
                        (anneeSelectionnee?.libelle ? ` · ${anneeSelectionnee.libelle}` : ` · ${anneeDebut}-${anneeDebut + 1}`)
                    }
                    onglet={onglet}
                    onChangeOnglet={onChangeOnglet}
                    calendrierPret={calendrierPret}
                >
                    {/* À l'étape 2, le droit est celui du Planning, pas l'administration
                        du département (cf. `lectureSeuleEvenements`) : le bandeau s'y tait. */}
                    {lectureSeule && etape !== 2 && (
                        <span className="pl-bandeau pl-bandeau-warn" style={{ margin: 0 }}>
                            <i className="fa-solid fa-eye" /> Lecture seule — vous n'administrez pas ce département
                        </span>
                    )}
                </PlanningTopbar>
            </div>

            <div className="pl-content">
                {/* Barre d'outils de la maquette : le titre de l'étape, son badge, SON
                    bouton (lot PA.1). L'étape 2 n'en a pas : chaque catégorie s'enregistre
                    dans sa propre fenêtre, un bouton de plus n'aurait rien à faire. */}
                <div className="pl-toolbar">
                    <h2>{definitionEtape?.titre}</h2>
                    {modifie[etape] && (
                        <span className="pl-badge pl-badge-warn">
                            <i className="fa-solid fa-circle" style={{ fontSize: 7 }} /> non sauvegardé
                        </span>
                    )}
                    <span className="pl-grow" />
                    {etape !== 2 && (
                        <button
                            className="pl-btn pl-btn-primary"
                            onClick={handleSauvegarder}
                            disabled={loading || lectureSeule || !allAnnees.length}
                            title={
                                lectureSeule ? "Vous n'administrez pas ce département"
                                    : (!allAnnees.length ? 'Aucune année universitaire : créez-en une dans Administration → Calendriers'
                                        : undefined)
                            }
                        >
                            {loading
                                ? <><i className="fa-solid fa-spinner fa-spin" /> Enregistrement…</>
                                : <><i className="fa-solid fa-floppy-disk" /> Sauvegarder</>}
                        </button>
                    )}
                </div>

                {/* Un échec d'enregistrement doit se voir : il ne part jamais dans la
                    console (leçon du lot 5.3 ter). */}
                {error && (
                    <div className="pl-bandeau pl-bandeau-warn">
                        <i className="fa-solid fa-triangle-exclamation" /> {error}
                    </div>
                )}
                {message && !error && (
                    <div className="pl-bandeau pl-bandeau-ok">
                        <i className="fa-solid fa-check" /> {message}
                    </div>
                )}

                {/* Les sous-onglets Configuration / Événements ont disparu (lot PA.1) :
                    ce sont désormais les étapes de la barre blanche. */}
                {etape === 2
                    ? <ParamEvenements
                        selectedDept={selectedDept}
                        lectureSeule={lectureSeule}
                        lectureSeuleEvenements={lectureSeuleEvenements}
                    />
                    : <ParamConfiguration etape={etape} {...proprietesVolets} />}
            </div>

            {/* ===== Garde-fou D-a : le récapitulatif AVANT d'écrire ===== */}
            {impactSuppression && (
                /* Lot 5.4 bis : ce bloc réécrivait à la main le balisage de
                   `ModalePlanning`, qui n'était pas exportée. Elle l'est désormais
                   (`ModalePlanning.js`) et sert aux trois écrans du module. Gain
                   secondaire : la touche Échap ferme aussi cette modale-ci. */
                <ModalePlanning
                    titre="Cette sauvegarde va supprimer des groupes"
                    icone="fa-triangle-exclamation"
                    taille="lg"
                    onFermer={() => setImpactSuppression(null)}
                    pied={
                        <>
                            <button className="pl-btn" onClick={() => setImpactSuppression(null)}>
                                Annuler
                            </button>
                            <button
                                className="pl-btn"
                                onClick={handleEnregistrerVersionDAbord}
                                disabled={versionEnCours || !anneeSelectionnee}
                                title="Enregistre une version restaurable du planning avant de supprimer"
                            >
                                {versionEnCours
                                    ? <><i className="fa-solid fa-spinner fa-spin" /> Enregistrement…</>
                                    : <><i className="fa-solid fa-clock-rotate-left" /> Enregistrer une version d'abord</>}
                            </button>
                            <button className="pl-btn pl-btn-danger" onClick={executerSauvegarde}>
                                <i className="fa-solid fa-check" /> Supprimer et enregistrer
                            </button>
                        </>
                    }
                >
                            <p>
                                {impactSuppression.impacts.length === 1
                                    ? 'Un groupe va être supprimé.'
                                    : `${impactSuppression.impacts.length} groupes vont être supprimés.`}
                                {' '}Voici ce qui y est rattaché :
                            </p>

                            <table className="pl-cfg-types" style={{ marginBottom: 12 }}>
                                <thead>
                                    <tr>
                                        <th>Groupe</th>
                                        <th>Périodes</th>
                                        <th>Étudiants</th>
                                        <th>Événements datés</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {impactSuppression.impacts.map(i => (
                                        <tr key={i.id}>
                                            <td><strong>{i.nom || i.code}</strong></td>
                                            <td>{i.periodes}</td>
                                            <td>{i.etudiants}</td>
                                            <td>{i.evenementsDates}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <p>
                                <strong>Les {impactSuppression.totaux.periodes} période(s)</strong> de ces
                                groupes seront <strong>définitivement supprimées</strong> du planning.
                                {impactSuppression.totaux.etudiants > 0 && (
                                    <> Les {impactSuppression.totaux.etudiants} étudiant(s) concerné(s) ne
                                    seront pas supprimés, mais <strong>détachés</strong> de leur groupe.</>
                                )}
                                {impactSuppression.totaux.evenementsDates > 0 && (
                                    <> {impactSuppression.totaux.evenementsDates} événement(s) daté(s)
                                    seront détachés de leur groupe.</>
                                )}
                            </p>
                            <p className="pl-hint">
                                Renommer un groupe ou un niveau ne supprime rien : seuls le compteur
                                « Groupes » baissé et les niveaux décochés suppriment.
                            </p>
                </ModalePlanning>
            )}

            {importModalOpen && selectedSousNiveauForImport && (
                <StudentImportModal
                    isOpen={importModalOpen}
                    departementId={selectedDept?.id}
                    sousNiveau={selectedSousNiveauForImport}
                    allSousNiveaux={[]}
                    onClose={() => setImportModalOpen(false)}
                    onImportSuccess={() => setImportModalOpen(false)}
                />
            )}
        </div>
    );
}

export default PlanningParametrage;
